import { supabase } from './supabase';

const SESSION_SELECT =
  'session_id, client_id, user_id, started_at, ended_at, duration_seconds, page_count, country, city, referrer';

const PAGE_VIEW_SELECT =
  'id, client_id, user_id, page_path, session_id, created_at';

/**
 * Fetch analytics sessions that started within [startIso, endIso].
 */
export async function fetchSessionsInRange(startIso, endIso) {
  const { data, error } = await supabase
    .from('analytics_sessions')
    .select(SESSION_SELECT)
    .gte('started_at', startIso)
    .lte('started_at', endIso)
    .order('started_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Fetch page views within [startIso, endIso].
 */
export async function fetchPageViewsInRange(startIso, endIso) {
  const { data, error } = await supabase
    .from('analytics_page_views')
    .select(PAGE_VIEW_SELECT)
    .gte('created_at', startIso)
    .lte('created_at', endIso)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * All users (requires admin RLS). Used for signups and search.
 */
export async function fetchAllUsers() {
  const { data, error } = await supabase
    .from('users')
    .select('id, name, created_at, is_admin')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function fetchAdminUsers() {
  const { data, error } = await supabase
    .from('users')
    .select('id, name, created_at, is_admin')
    .eq('is_admin', true)
    .order('name', { ascending: true });

  if (error) throw error;
  return data || [];
}

/** Strip ilike wildcards from user input */
const sanitizeIlikeQuery = (value) => value.replace(/[%_\\]/g, '');

export async function searchUsersByName(query, limit = 5) {
  const q = sanitizeIlikeQuery((query || '').trim());
  if (!q) return [];

  const { data, error } = await supabase
    .from('users')
    .select('id, name, created_at, is_admin')
    .ilike('name', `%${q}%`)
    .order('name', { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

/** Sessions and page views for one authenticated user in range */
export async function fetchUserActivity(userId, startIso, endIso) {
  const [sessionsRes, viewsRes] = await Promise.all([
    supabase
      .from('analytics_sessions')
      .select(SESSION_SELECT)
      .eq('user_id', userId)
      .gte('started_at', startIso)
      .lte('started_at', endIso)
      .order('started_at', { ascending: false }),
    supabase
      .from('analytics_page_views')
      .select(PAGE_VIEW_SELECT)
      .eq('user_id', userId)
      .gte('created_at', startIso)
      .lte('created_at', endIso)
      .order('created_at', { ascending: false }),
  ]);

  if (sessionsRes.error) throw sessionsRes.error;
  if (viewsRes.error) throw viewsRes.error;

  return {
    sessions: sessionsRes.data || [],
    pageViews: viewsRes.data || [],
  };
}
